'use client'

import { useQuery } from '@apollo/client/react'
import { useEffect, useMemo, useRef } from 'react'
import { createContainer } from 'unstated-next'

import { gql } from '../../__generated__/graphql/catalog'
import { QueryStatus } from '../../lib/hooks/types'
import { deriveErrorTypes } from '../../lib/utils/errors'
import { User } from '../_config/User.config'

import { useAuthentication } from './AuthenticationProvider.client'
import { useGraphqlClients } from './GraphqlClientsProvider.client'

export type UseUserOutput = {
  user: User | undefined
  userStatus: QueryStatus
}

const useUserImpl = (serverProvidedUser: User | undefined) => {
  const { isLoggedIn, refreshAuthTokens } = useAuthentication()
  const { personalizedClient } = useGraphqlClients()

  const {
    data,
    loading,
    error,
    refetch: refetchUser,
  } = useQuery(
    gql(`
      query GetUserForClientSidePageView {
        my {
          id
          email
          shopifyId
          profile {
            id
            firstName
            lastName
            phone
            ordersCount
            stylePreference
          }
          activeTeamStore {
            id
            name
            organizationCollection {
              id
              catalogCollection {
                id
                handle
              }
            }
          }
        }
        customerProfile {
          id
          sizePreferences {
            id
            gender
            category
            size
            fit
          }
        }
      }`),
    {
      client: personalizedClient,
      skip: !isLoggedIn || !!serverProvidedUser,
    }
  )

  // Self heal invalid tokens. This would happen if the auth token is for some reason not valid
  // but the timing in the JWT did not indicate we needed to refresh it. So middleware did not run
  // an auth refresh. In that case, API calls fail but we could refresh. refreshAuthTokens will clear
  // auth cookies if it is unable to refresh them.
  const refreshWasAttemptedRef = useRef(false)
  useEffect(() => {
    if (
      isLoggedIn &&
      deriveErrorTypes(error).includes('UNAUTHENTICATED') &&
      refreshWasAttemptedRef.current === false
    ) {
      refreshWasAttemptedRef.current = true
      refreshAuthTokens().then(() => {
        refetchUser()
      })
    }
  }, [error, isLoggedIn, refetchUser, refreshAuthTokens])

  const clientProvidedUser = useMemo<UseUserOutput['user']>(() => {
    // TODO: Fix this auto-generated suppression.
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (error || !data?.my?.id) return undefined
    return {
      id: data.my.id,
      email: data.my.email,
      shopifyId: data.my.shopifyId?.replace('gid://shopify/Customer/', '') ?? undefined,
      profile: {
        firstName: data.my.profile.firstName ?? 'FIGS Customer',
        lastName: data.my.profile.lastName ?? undefined,
        phone: data.my.profile.phone ?? undefined,
        ordersCount: data.my.profile.ordersCount,
        preferences: {
          size: data.customerProfile?.sizePreferences || [],
          style: data.my.profile.stylePreference,
        },
      },
      activeTeamStore: data.my.activeTeamStore,
    }
  }, [data, error])

  const user = useMemo<UseUserOutput['user']>(() => {
    return clientProvidedUser ?? serverProvidedUser
  }, [clientProvidedUser, serverProvidedUser])

  const userStatus = useMemo<UseUserOutput['userStatus']>(() => {
    if (user) return 'resolved'
    if (loading) return 'pending'
    if (error) return 'rejected'
    return 'resolved'
  }, [loading, user, error])

  return {
    user,
    userStatus,
  }
}

const UserContainer = createContainer(useUserImpl)
UserContainer.Provider.displayName = 'UserProviderImpl'
export const UserProvider = UserContainer.Provider
export const useUser = UserContainer.useContainer
